import { createContext, PropsWithChildren, useContext, useState } from 'react';

import { INSTANCE_COMPATIBILITY_MAPS } from './InstanceCompatibilityMap';
import { getCompatibleInstances } from './utils';

import { useCluster } from '@cast/shared-cluster-context-data-access';

export type ClmInstanceSelection = {
  baseInstance: string;
  compatibleInstances: string[];
};

type ClmCompatibleFamiliesPickerState = {
  availableInstances: string[];
  baseInstance?: string;
  setBaseInstance: (instance?: string) => void;
  selectedInstances: string[];
  setSelectedInstances: (instances: string[]) => void;
  activeStep: 0 | 1;
};

const ClmCompatibleFamiliesPickerContext =
  createContext<ClmCompatibleFamiliesPickerState>(null as never);

type ClmCompatibleFamiliesPickerProviderProps = PropsWithChildren<{
  availableInstances: string[];
  initialSelection?: ClmInstanceSelection;
}>;

export const ClmCompatibleFamiliesPickerProvider = ({
  availableInstances,
  initialSelection,
  children,
}: ClmCompatibleFamiliesPickerProviderProps) => {
  const { cluster } = useCluster();

  const [baseInstance, setBaseInstance] = useState<string | undefined>(
    initialSelection?.baseInstance
  );
  const [selectedInstances, setSelectedInstances] = useState<string[]>(() => {
    const initialBaseInstance = initialSelection?.baseInstance;
    const initialCompatibleInstances =
      initialSelection?.compatibleInstances ?? [];

    if (!initialBaseInstance) {
      return [];
    }

    const compatibilityMap = INSTANCE_COMPATIBILITY_MAPS[cluster.providerType];
    if (!compatibilityMap) {
      return [];
    }

    const compatibleWithBase = getCompatibleInstances({
      instance: initialBaseInstance,
      compatibilityMap: compatibilityMap,
    });

    return initialCompatibleInstances.filter((candidateInstance) =>
      compatibleWithBase.includes(candidateInstance)
    );
  });
  const [activeStep, setActiveStep] = useState<0 | 1>(baseInstance ? 1 : 0);

  const handleBaseInstanceSelect = (newInstance?: string) => {
    setBaseInstance(newInstance);
    setActiveStep(newInstance ? 1 : 0);
    setSelectedInstances([]);
  };

  return (
    <ClmCompatibleFamiliesPickerContext.Provider
      value={{
        availableInstances,
        baseInstance,
        setBaseInstance: handleBaseInstanceSelect,
        selectedInstances,
        setSelectedInstances,
        activeStep,
      }}
    >
      {children}
    </ClmCompatibleFamiliesPickerContext.Provider>
  );
};

export const useClmCompatibleFamiliesPicker = () => {
  return useContext(ClmCompatibleFamiliesPickerContext);
};
